// LEGACY — superseded by features/client-meetings/api/client.ts. Scheduled for deletion in 2 sprints.
import { APIClient } from "../helpers/api_helper";
import { authService } from "../services/authService";
import * as url from "../helpers/url_helper";
import axios from "axios";
import config from "../config";
import {
  ClientMeeting,
  ClientMeetingPayload,
  MEETING_STATUS_MAP,
} from "../interfaces/ClientMeeting";
import { ClientMeetingsResponse } from "../slices/clientMeetings/types";

const api = new APIClient();
const { api: apiConfig } = config;

// Helper to get auth token
const getAuthToken = () => {
  return authService.getToken();
};

// Check if we should use the fake backend
export const getAllClientMeetings = (
  pageNumber: number = 1,
  pageSize: number = 20,
  clientId?: string,
  startDate?: string,
  endDate?: string
): Promise<ClientMeetingsResponse> => {
  
  // Real API call with query parameters
  // Default: fetch meetings from today to upcoming 30 days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thirtyDaysLater = new Date(today);
  thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
  
  const params: any = {
    pageNumber,
    pageSize,
    startDate: startDate || today.toISOString().split('T')[0],
    endDate: endDate || thirtyDaysLater.toISOString().split('T')[0],
  };
  if (clientId) {
    params.clientId = clientId;
  }
  
  return api.get(url.GET_CLIENT_MEETINGS, params).then((response: any) => {
    // Handle API response wrapper if present
    // API might return: { result: { items: [...], pageNumber: 1, ... } } or directly { items: [...], pageNumber: 1, ... }
    if (response?.result) {
      return response.result as ClientMeetingsResponse;
    }
    
    // If response has items directly, return as is
    if (response?.items) {
      return response as ClientMeetingsResponse;
    }
    
    // Fallback: return response as is (might be wrapped differently)
    return response as ClientMeetingsResponse;
  }) as unknown as Promise<ClientMeetingsResponse>;
};

export const getClientMeetingById = (id: string): Promise<ClientMeeting> => {
  return api.get(`${url.GET_CLIENT_MEETING_BY_ID}/${id}`, {}) as unknown as Promise<ClientMeeting>;
};

// Get meeting data for edit - includes participants
export const getClientMeetingForEdit = (
  id: string,
  clientId: string
): Promise<any> => {
  // GET /ClientMeeting/{id}/ForEdit?clientId={clientId}
  const apiUrl = `${url.GET_CLIENT_MEETING_FOR_EDIT}/${id}/ForEdit?clientId=${clientId}`;
  // Pass undefined instead of {} to avoid adding extra ? when URL already has query params
  return api.get(apiUrl, undefined) as unknown as Promise<any>;
};

export const createClientMeeting = (
  data: ClientMeetingPayload
): Promise<ClientMeeting> => {
  
  // Always use multipart/form-data for consistency
  const token = getAuthToken();
  const formData = new FormData();
  
  // Append all form fields
  if (data.tenantId) formData.append("TenantId", data.tenantId);
  if (data.clientId) formData.append("ClientId", data.clientId);
  if (data.meetingTitle) formData.append("MeetingTitle", data.meetingTitle);
  if (data.meetingDescription) formData.append("MeetingDescription", data.meetingDescription || "");
  if (data.meetingDate) formData.append("MeetingDate", data.meetingDate);
  if (data.meetingStartTime) formData.append("MeetingStartTime", data.meetingStartTime);
  if (data.meetingEndTime) formData.append("MeetingEndTime", data.meetingEndTime);
  if (data.meetingLocation) formData.append("MeetingLocation", data.meetingLocation || "");
  if (data.meetingType !== undefined) formData.append("MeetingType", String(data.meetingType));
  if (data.organizerUserId) formData.append("OrganizerUserId", data.organizerUserId);
  if (data.tenantUserIds && data.tenantUserIds.length > 0) {
    data.tenantUserIds.forEach((userId) => {
      formData.append("TenantUserIds", userId);
    });
  }
  if (data.clientContactIds && data.clientContactIds.length > 0) {
    data.clientContactIds.forEach((contactId) => {
      formData.append("ClientContactIds", contactId);
    });
  }
  if (data.externalAttendees) formData.append("ExternalAttendees", data.externalAttendees || "");
  if (data.reminderMinutes !== undefined && data.reminderMinutes !== null) {
    formData.append("ReminderMinutes", String(data.reminderMinutes));
  } else if (data.reminderMinutes === null) {
    formData.append("ReminderMinutes", "");
  }
  
  // Append attachment file if provided
  if (data.attachment && data.attachment instanceof File) {
    formData.append("Attachment", data.attachment);
  }
  // Append attachment ID if replacing existing attachment
  if (data.attachmentId) {
    formData.append("AttachmentId", data.attachmentId);
  }
  
  return axios.post(
    `${apiConfig.API_URL}${url.ADD_NEW_CLIENT_MEETING}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 seconds for file upload
    }
  ).then((response: any) => {
    const responseData = response.data || response;
    // Handle API response wrapper
    if (responseData.result?.ClientMeeting) {
      return responseData.result.ClientMeeting as ClientMeeting;
    }
    if (responseData.result) {
      return responseData.result as ClientMeeting;
    }
    return responseData as unknown as ClientMeeting;
  }) as unknown as Promise<ClientMeeting>;
};

export const updateClientMeeting = (
  id: string,
  data: Partial<ClientMeeting>
): Promise<ClientMeeting> => {
  
  // PUT payload - only editable fields (no tenant/client IDs)
  const payload = {
    meetingTitle: data.meetingTitle,
    meetingDescription: data.meetingDescription,
    meetingDate: data.meetingDate,
    meetingStartTime: data.meetingStartTime,
    meetingEndTime: data.meetingEndTime,
    meetingLocation: data.meetingLocation,
    meetingType: data.meetingType,
  };
  return api
    .put(`${url.UPDATE_CLIENT_MEETING}/${id}`, payload)
    .then((response: any) => {
      // Handle API response wrapper
      if (response.result?.ClientMeeting) {
        return response.result.ClientMeeting;
      }
      return response as unknown as ClientMeeting;
    }) as unknown as Promise<ClientMeeting>;
};

// Update meeting for edit - includes full payload with participants
export const updateClientMeetingForEdit = (
  id: string,
  clientId: string,
  data: ClientMeetingPayload
): Promise<ClientMeeting> => {
  
  // Always use multipart/form-data for consistency
  const token = getAuthToken();
  const apiUrl = `${url.UPDATE_CLIENT_MEETING_FOR_EDIT}/${id}/ForEdit?clientId=${clientId}`;
  const formData = new FormData();
  
  // Append all form fields
  if (data.tenantId) formData.append("TenantId", data.tenantId);
  if (data.clientId) formData.append("ClientId", data.clientId);
  if (data.meetingTitle) formData.append("MeetingTitle", data.meetingTitle);
  if (data.meetingDescription) formData.append("MeetingDescription", data.meetingDescription || "");
  if (data.meetingDate) formData.append("MeetingDate", data.meetingDate);
  if (data.meetingStartTime) formData.append("MeetingStartTime", data.meetingStartTime);
  if (data.meetingEndTime) formData.append("MeetingEndTime", data.meetingEndTime);
  if (data.meetingLocation) formData.append("MeetingLocation", data.meetingLocation || "");
  if (data.meetingType !== undefined) formData.append("MeetingType", String(data.meetingType));
  if (data.organizerUserId) formData.append("OrganizerUserId", data.organizerUserId);
  if (data.tenantUserIds && data.tenantUserIds.length > 0) {
    data.tenantUserIds.forEach((userId) => {
      formData.append("TenantUserIds", userId);
    });
  }
  if (data.clientContactIds && data.clientContactIds.length > 0) {
    data.clientContactIds.forEach((contactId) => {
      formData.append("ClientContactIds", contactId);
    });
  }
  if (data.externalAttendees) formData.append("ExternalAttendees", data.externalAttendees || "");
  if (data.reminderMinutes !== undefined && data.reminderMinutes !== null) {
    formData.append("ReminderMinutes", String(data.reminderMinutes));
  } else if (data.reminderMinutes === null) {
    formData.append("ReminderMinutes", "");
  }
  // Append meeting status if provided (for UpdateForEdit API)
  if (data.meetingStatus !== undefined) {
    formData.append("MeetingStatus", String(data.meetingStatus));
  }
  
  // Append attachment file if provided
  if (data.attachment && data.attachment instanceof File) {
    formData.append("Attachment", data.attachment);
  }
  // Append attachment ID if replacing existing attachment
  if (data.attachmentId) {
    formData.append("AttachmentId", data.attachmentId);
  }
  
  console.log("API URL:", apiUrl);
  console.log("Using multipart/form-data");
  
  return axios.put(
    `${apiConfig.API_URL}${apiUrl}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 seconds for file upload
    }
  ).then((response: any) => {
    const responseData = response.data || response;
    console.log("API Response:", responseData);
    // Handle API response wrapper - response.result contains the meeting
    // API returns: { id, isSuccess, message, result: { ...meeting data... } }
    if (responseData?.result) {
      return responseData.result as ClientMeeting;
    }
    // If no wrapper, return response directly
    return responseData as unknown as ClientMeeting;
  })
  .catch((error: any) => {
    // Re-throw with more context
    console.error("Error updating meeting:", error);
    throw error;
  }) as unknown as Promise<ClientMeeting>;
};

export const rescheduleClientMeeting = (
  id: string,
  newDate: string,
  newStartTime: string,
  newEndTime: string
): Promise<ClientMeeting> => {
  
  const payload = {
    newDate,
    newStartTime,
    newEndTime,
  };
  return api
    .put(`${url.RESCHEDULE_CLIENT_MEETING}/${id}/Reschedule`, payload)
    .then((response: any) => {
      // Handle API response wrapper - response.result contains the meeting
      // API returns: { id, isSuccess, message, result: { ...meeting data... } }
      if (process.env.NODE_ENV === 'development') {
        console.log("Reschedule API Response:", response);
      }
      
      if (response?.result) {
        // Check if result contains ClientMeeting nested object
        if (response.result.ClientMeeting) {
          const meeting = response.result.ClientMeeting as ClientMeeting;
          if (process.env.NODE_ENV === 'development') {
            console.log("Extracted meeting from response.result.ClientMeeting:", meeting);
            console.log("Meeting status:", meeting.meetingStatus, `(${MEETING_STATUS_MAP[meeting.meetingStatus] || 'Unknown'})`);
          }
          return meeting;
        }
        // Otherwise, result is the meeting data directly
        const meeting = response.result as ClientMeeting;
        if (process.env.NODE_ENV === 'development') {
          console.log("Extracted meeting from response.result:", meeting);
          console.log("Meeting status:", meeting.meetingStatus, `(${MEETING_STATUS_MAP[meeting.meetingStatus] || 'Unknown'})`);
        }
        return meeting;
      }
      // If no wrapper, return response directly
      const meeting = response as unknown as ClientMeeting;
      if (process.env.NODE_ENV === 'development') {
        console.log("Using response directly as meeting:", meeting);
        console.log("Meeting status:", meeting?.meetingStatus, `(${MEETING_STATUS_MAP[meeting?.meetingStatus] || 'Unknown'})`);
      }
      return meeting;
    }) as unknown as Promise<ClientMeeting>;
};

export const cancelClientMeeting = (id: string): Promise<ClientMeeting> => {
  
  return api
    .put(`${url.CANCEL_CLIENT_MEETING}/${id}/Cancel`, {})
    .then((response: any) => {
      // Handle API response wrapper - response.result contains the meeting
      // API returns: { id, isSuccess, message, result: { ...meeting data... } }
      if (process.env.NODE_ENV === 'development') {
        console.log("Cancel API Response:", response);
      }
      
      if (response?.result) {
        // Check if result contains ClientMeeting nested object
        if (response.result.ClientMeeting) {
          const meeting = response.result.ClientMeeting as ClientMeeting;
          if (process.env.NODE_ENV === 'development') {
            console.log("Extracted meeting from response.result.ClientMeeting:", meeting);
            console.log("Meeting status:", meeting.meetingStatus, `(${MEETING_STATUS_MAP[meeting.meetingStatus] || 'Unknown'})`);
          }
          return meeting;
        }
        // Otherwise, result is the meeting data directly
        const meeting = response.result as ClientMeeting;
        if (process.env.NODE_ENV === 'development') {
          console.log("Extracted meeting from response.result:", meeting);
          console.log("Meeting status:", meeting.meetingStatus, `(${MEETING_STATUS_MAP[meeting.meetingStatus] || 'Unknown'})`);
        }
        return meeting;
      }
      // If no wrapper, return response directly
      const meeting = response as unknown as ClientMeeting;
      if (process.env.NODE_ENV === 'development') {
        console.log("Using response directly as meeting:", meeting);
        console.log("Meeting status:", meeting?.meetingStatus, `(${MEETING_STATUS_MAP[meeting?.meetingStatus] || 'Unknown'})`);
      }
      return meeting;
    }) as unknown as Promise<ClientMeeting>;
};

export const completeClientMeeting = (
  id: string,
  meetingOutcomeMOM: string,
  attachment?: File
): Promise<ClientMeeting> => {
  const token = getAuthToken();
  const apiUrl = `${url.COMPLETE_CLIENT_MEETING}/${id}/Complete`;

  if (attachment && attachment instanceof File) {
    // Use multipart/form-data when attachment is present
    const formData = new FormData();
    formData.append("MeetingOutcomeMOM", meetingOutcomeMOM || "");
    formData.append("Attachment", attachment);

    return axios.put(
      `${apiConfig.API_URL}${apiUrl}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000,
      }
    ).then((response: any) => {
      const responseData = response.data || response;
      if (responseData?.result) {
        return responseData.result as ClientMeeting;
      }
      return responseData as unknown as ClientMeeting;
    })
    .catch((error: any) => {
      console.error("Error completing meeting:", error);
      throw error;
    }) as unknown as Promise<ClientMeeting>;
  } else {
    // Use JSON when no attachment
    const payload = {
      MeetingOutcomeMOM: meetingOutcomeMOM || "",
    };

    return axios.put(
      `${apiConfig.API_URL}${apiUrl}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        timeout: 30000,
      }
    ).then((response: any) => {
      const responseData = response.data || response;
      if (responseData?.result) {
        return responseData.result as ClientMeeting;
      }
      return responseData as unknown as ClientMeeting;
    })
    .catch((error: any) => {
      console.error("Error completing meeting:", error);
      throw error;
    }) as unknown as Promise<ClientMeeting>;
  }
};

export const deleteClientMeeting = (id: string): Promise<void> => {
  
  return api.delete(`${url.DELETE_CLIENT_MEETING}/${id}`) as unknown as Promise<void>;
};

export const deleteClientMeetingAttachment = (attachmentId: string): Promise<void> => {
  const token = getAuthToken();
  return axios.delete(`${apiConfig.API_URL}${url.DELETE_CLIENT_MEETING_ATTACHMENT}/${attachmentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  }) as unknown as Promise<void>;
};

